import { inject, Injectable } from '@angular/core';
import { AuthenticationStateStore } from './auth.state';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { catchError, filter, from, iif, map, mergeMap, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { canDecodeURIComponent } from './utils';
import { AUTH_CONFIG, AuthConfigurationProperties } from '@unicup-ticketing/injection-context';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly state = inject(AuthenticationStateStore);
  private readonly oauthService = inject(OAuthService);
  private readonly router = inject(Router);

  private readonly config: AuthConfig;

  private replaceHistoryEntryOnRedirect = false;

  constructor() {
    const authConfig = inject<AuthConfigurationProperties>(AUTH_CONFIG);

    this.config = {
      issuer: authConfig.issuer,
      redirectUri: authConfig.redirectUri,
      clientId: authConfig.clientId,
      responseType: 'code',
      scope: `profile email basic offline_access ${authConfig.scopes.join(' ')}`,
      showDebugInformation: authConfig.verboseLogging,
      waitForTokenInMsec: 2000,
      timeoutFactor: 0.75,
      openUri: uri => {
        if (this.replaceHistoryEntryOnRedirect) {
          location.replace(uri);
        } else {
          location.href = uri;
        }
      },
    };

    this.oauthService.configure(this.config);
    this.oauthService.setupAutomaticSilentRefresh();

    this.oauthService.events.subscribe(event => {
      if (this.config.showDebugInformation) {
        if (event instanceof OAuthErrorEvent) {
          console.info('OAuthErrorEvent Object:', event);
        } else {
          console.info('OAuthEvent Object:', event);
        }
      }
    });

    window.addEventListener('storage', event => {
      if (event.key !== 'access_token' && event.key !== 'id_token') {
        return;
      }

      console.warn('Noticed changes to stored tokens (most likely from another tab)');
      if (!this.allTokensValid()) {
        this.state.setIsAuthenticated(false);
        this.triggerAuthentication().subscribe();
      }
    });

    this.oauthService.events
      .pipe(tap(() => this.state.setAuthenticationInProgress(this.oauthService.hasValidAccessToken())))
      .subscribe();

    this.oauthService.events
      .pipe(
        filter(e => ['token_received'].includes(e.type)),
        mergeMap(() => {
          const state = this.oauthService.state;
          if (state != undefined && state != '' && canDecodeURIComponent(state)) {
            return from(this.router.navigateByUrl(decodeURIComponent(state)));
          }
          return of(void 0);
        }),
      )
      .subscribe();

    this.oauthService.events
      .pipe(
        filter(e => ['session_terminated', 'session_error'].includes(e.type)),
        tap(() => this.state.setAuthenticationInProgress(false)),
      )
      .subscribe();

    this.isAuthenticated().subscribe(); //check if the user has already valid tokens while we construct the service
  }

  /**
   * @param requestedUrl url to return to after sign in
   * @param replaceHistoryEntry replace the current history entry instead of pushing the identity provider on top,
   * so that navigating back does not return to a page that would immediately trigger the sign in again
   */
  public triggerAuthentication(requestedUrl?: string, replaceHistoryEntry = false): Observable<boolean> {
    this.state.setAuthenticationInProgress(true);
    return this.loadDiscoveryDocument().pipe(
      switchMap(result =>
        iif(
          () => result,
          of(void 0).pipe(
            tap(() => this.login(requestedUrl, replaceHistoryEntry)),
            map(() => true),
          ),
          throwError(() => {
            this.state.setAuthenticationInProgress(false);
            return new Error('Discovery Document fetching threw error. Aborting sign in.');
          }),
        ),
      ),
      tap(() => this.state.setAuthenticationInProgress(false)),
    );
  }

  public parseIdToken(): void {
    const claims = this.oauthService.getIdentityClaims();
    if (claims != null) {
      const sub = claims['sub'];
      const givenName = claims['given_name'];
      const surname = claims['family_name'];
      const email = claims['email'];

      if (givenName !== undefined && surname !== undefined && sub !== undefined) {
        this.state.setIdTokenClaims({
          user: { givenName, surname },
          email,
          sub,
          fachinfo: claims['rwthFachInfo'],
        });
      } else {
        this.state.setIdTokenClaims(undefined);
        console.error('Could not set user on token received event.');
      }
    }
    return undefined;
  }

  public isAuthenticated(): Observable<boolean> {
    return this.loadDiscoveryDocument().pipe(map(() => this.allTokensValid()));
  }

  public handleLoginRedirect(): Observable<boolean> {
    this.state.setAuthenticationInProgress(true);
    return from(this.oauthService.loadDiscoveryDocumentAndTryLogin()).pipe(
      switchMap(result => {
        if (result) {
          this.state.setIsDiscoveryDocumentLoaded(true);
          if (this.oauthService.hasValidAccessToken() && this.oauthService.hasValidIdToken()) {
            this.state.setIsAuthenticated(true);
            return of(true);
          } else {
            // No (valid) tokens, e.g. the callback url was opened without a code
            this.state.setAuthenticationInProgress(false);
          }
        } else {
          this.state.setAuthenticationInProgress(false);
        }
        return of(false);
      }),
      catchError(() => {
        this.state.setAuthenticationInProgress(false);
        return of(false);
      }),
    );
  }

  private loadDiscoveryDocument(): Observable<boolean> {
    if (!this.state.discoveryDocumentLoaded()) {
      return from(this.oauthService.loadDiscoveryDocument()).pipe(
        tap(() => this.state.setIsDiscoveryDocumentLoaded(true)),
        map(() => true),
        catchError(() => {
          console.error('Error while retrieving oid configuration document.');
          return of(false);
        }),
      );
    } else {
      return of(true);
    }
  }

  private allTokensValid(): boolean {
    if (this.oauthService.hasValidIdToken() && this.oauthService.hasValidAccessToken()) {
      this.parseIdToken();
      this.state.setIsAuthenticated(true);
      return true;
    }
    return false;
  }

  private login(requestedUrl?: string, replaceHistoryEntry = false): void {
    if (!this.allTokensValid()) {
      this.replaceHistoryEntryOnRedirect = replaceHistoryEntry;
      this.oauthService.initCodeFlow(requestedUrl);
    } else {
      this.state.setIsAuthenticated(true);
    }
  }
}
